import React, { useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { PersonaCardBody } from '../../../../personas/PersonaCardBody';
import { ExecutionSection } from './ExecutionSection';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { usePersonaById } from '../../../../../hooks/usePersonaById';
import { useNodeDimensions } from '../../hooks/useNodeDimensions';
import { useTabFocalTrigger } from '../../hooks/useTabFocalTrigger';
import { peerAriaLabel } from '../ariaLabels';
import type { PeerDagData } from '../../types';

// v0.4 Task 6: rich peer card.
//
// Composition: PersonaCardBody (fetched via usePersonaById) +
// ExecutionSection footer + running-ring when in flight. Falls back
// to a minimal placeholder if the persona fetch is still loading or
// 404s — the card still lands at its target dims and tokens/cost
// are visible from `data` even before the persona resolves.
//
// wrapperRef lives here so Task 10 can wire useNodeDimensions for
// ResizeObserver measurement.

export interface PeerNodeRichProps {
  id: string;
  data: PeerDagData;
}

export const PeerNodeRich: React.FC<PeerNodeRichProps> = ({ id, data }) => {
  const { darkMode } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useNodeDimensions(id, wrapperRef);
  const { data: persona } = usePersonaById(data.personaId);
  const execId = `exec-${id}`;

  const tabTrigger = useTabFocalTrigger(data.turnKey);
  const runState = data.runState ?? 'completed';
  const running = runState === 'running' || data.status === 'running';
  // Ported from v0.3 PeerNodeCompact so in-flight UX stays consistent
  // whether the turn is focal (rich) or not (compact).
  const ringClass = running ? 'chatdag-peer-running-ring' : '';

  return (
    <div
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      aria-label={peerAriaLabel(data)}
      aria-describedby={execId}
      onFocus={tabTrigger.onFocus}
      onBlur={tabTrigger.onBlur}
      className={`w-[360px] rounded-lg shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 ${
        darkMode
          ? 'bg-gray-800 border border-gray-700'
          : 'bg-white border border-gray-200'
      } ${ringClass}`}
      data-node-id={id}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="l"
        className="!opacity-0 !pointer-events-none"
      />
      {persona ? (
        <PersonaCardBody persona={persona} />
      ) : (
        <div
          className={`p-4 ${
            darkMode ? 'bg-gray-700' : 'bg-gray-50'
          } border-b ${
            darkMode ? 'border-gray-600' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center">
            <div
              className={`w-12 h-12 rounded-full ${
                darkMode ? 'bg-gray-600' : 'bg-gray-200'
              } flex items-center justify-center`}
            >
              <span
                className={`text-xl ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                ?
              </span>
            </div>
            <div className="ml-3">
              <h3
                className={`font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {data.personaName ?? 'Unknown peer'}
              </h3>
              <p
                className={`text-xs italic ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
              >
                loading…
              </p>
            </div>
          </div>
        </div>
      )}
      <ExecutionSection
        model={data.model}
        totalTokens={data.totalTokens}
        promptTokens={data.promptTokens}
        completionTokens={data.completionTokens}
        costUsd={data.costUsd}
        durationMs={data.durationMs}
        prompt={data.prompt}
        response={data.response}
        error={data.error}
        status={data.status}
        execSectionId={execId}
      />
    </div>
  );
};
